import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getDrawingDimFontPx,
  measureLiveDimensionOverlays,
} from '../lib/drawingDimensionOverlays';
import {
  prepareSvgHtmlForEmbed,
  prepareSvgHtmlForDisplay,
  svgHtmlToDataUrl,
} from '../lib/svgCapture';

/**
 * Quote drawings: frame as rasterized SVG (no dimension text),
 * labels as crisp HTML overlays so PDF export stays sharp.
 */
const VmkQuoteDrawing = ({
  svgHtml,
  width,
  height,
  compact = false,
  skipDimensionOverlays = false,
  hideHeightDimensionOverlay = false,
}) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [overlays, setOverlays] = useState([]);
  const [displayDataUrl, setDisplayDataUrl] = useState(null);
  const [imgReady, setImgReady] = useState(false);
  const fontPx = getDrawingDimFontPx(compact);

  const preparedSvg = useMemo(
    () => (svgHtml ? prepareSvgHtmlForEmbed(svgHtml, { saved: true }) : null),
    [svgHtml]
  );

  useEffect(() => {
    if (!preparedSvg) {
      setDisplayDataUrl(null);
      setImgReady(false);
      return undefined;
    }

    let cancelled = false;
    setImgReady(false);

    // Strip labels from the visible drawing — HTML overlays replace them
    prepareSvgHtmlForDisplay(preparedSvg, {
      saved: true,
      stripDimensionLabels: !skipDimensionOverlays,
    }).then((readySvg) => {
      if (cancelled) return;
      setDisplayDataUrl(
        svgHtmlToDataUrl(readySvg || preparedSvg, { saved: true, alreadyPrepared: true })
      );
    });

    return () => {
      cancelled = true;
    };
  }, [preparedSvg, skipDimensionOverlays]);

  const updateOverlays = useCallback(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    const svg = wrap?.querySelector('.vmk-drawing-measure svg');

    if (skipDimensionOverlays || !wrap || !preparedSvg || !width || !svg) {
      setOverlays([]);
      return;
    }

    if (img && (!imgReady || img.clientWidth < 1)) {
      return;
    }

    setOverlays(
      measureLiveDimensionOverlays(wrap, svg, width, height, img || null, {
        hideHeight: hideHeightDimensionOverlay,
      })
    );
  }, [preparedSvg, width, height, skipDimensionOverlays, hideHeightDimensionOverlay, imgReady]);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setImgReady(true);
    }
  }, [displayDataUrl]);

  useLayoutEffect(() => {
    updateOverlays();
    requestAnimationFrame(updateOverlays);

    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap) return undefined;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateOverlays);
    });
    observer.observe(wrap);
    if (img) observer.observe(img);

    const svg = wrap.querySelector('.vmk-drawing-measure svg');
    if (svg) observer.observe(svg);

    const onRemasure = () => {
      requestAnimationFrame(updateOverlays);
    };
    window.addEventListener('vmk-remasure-dim-overlays', onRemasure);

    return () => {
      observer.disconnect();
      window.removeEventListener('vmk-remasure-dim-overlays', onRemasure);
    };
  }, [updateOverlays, displayDataUrl]);

  const handleImgLoad = () => {
    setImgReady(true);
  };

  if (!preparedSvg) return null;

  return (
    <div
      ref={wrapRef}
      className={`vmk-drawing-wrap relative w-full${compact ? ' vmk-drawing-wrap--compact' : ''}`}
      data-dim-width={width || ''}
      data-dim-height={height || ''}
      data-dim-hide-height={hideHeightDimensionOverlay ? 'true' : undefined}
    >
      <div className="vmk-drawing-inner w-full flex justify-center items-start relative">
        {displayDataUrl ? (
          <img
            ref={imgRef}
            src={displayDataUrl}
            alt=""
            className="vmk-drawing-img max-w-full w-auto h-auto object-contain"
            draggable={false}
            onLoad={handleImgLoad}
          />
        ) : (
          <div className="vmk-drawing-img-placeholder w-full min-h-[120px]" aria-hidden />
        )}
        {!skipDimensionOverlays ? (
          <div
            className="vmk-drawing-measure absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: preparedSvg }}
          />
        ) : null}
      </div>
      {overlays.length > 0 && (
        <div className="vmk-dim-labels" aria-hidden="true">
          {overlays.map((overlay) => (
            <span
              key={overlay.id}
              className={[
                'vmk-dim-label-overlay',
                overlay.tier === 'height-part-left'
                  ? 'vmk-dim-label-overlay--height-part-left'
                  : overlay.tier === 'height-part'
                    ? 'vmk-dim-label-overlay--height-part'
                    : overlay.align === 'start' || overlay.tier === 'height'
                      ? 'vmk-dim-label-overlay--height'
                      : '',
                overlay.tier === 'part' ? 'vmk-dim-label-overlay--part' : '',
                overlay.tier === 'part-below' ? 'vmk-dim-label-overlay--part-below' : '',
                overlay.tier === 'main' ? 'vmk-dim-label-overlay--main' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                left: `${overlay.left}px`,
                top: `${overlay.top}px`,
                fontSize:
                  overlay.tier === 'part' ||
                  overlay.tier === 'part-below' ||
                  overlay.tier === 'height-part' ||
                  overlay.tier === 'height-part-left'
                    ? '7px'
                    : `${fontPx}px`,
              }}
            >
              {overlay.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default VmkQuoteDrawing;
